import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { FileUploadGallery, UploadedFile } from '@campfire/file-upload-gallery';
import { Box, Grid, Typography } from '@material-ui/core';
import { ShortTextRounded } from '@material-ui/icons';
import { convertFromRaw, EditorState } from 'draft-js';
import React, { useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import { AlertCard, getAlertCardColors } from '../../cards/alert-card/AlertCard';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { TaskItemsDeserialize } from '../../../screens/admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import {
  AddressTaskFormikField,
  AttachmentTaskFormikField,
  CheckboxTaskFormikField,
  DateTaskFormikField,
  TimeTaskFormikField,
  DropdownTaskFormikField,
  EmailTaskFormikField,
  UrlTaskFormikField,
  YesNoTaskFormikField,
  LinearScaleTaskFormikField,
  NpsTaskFormikField,
  LongTextTaskFormikField,
  MultipleChoiceTaskFormikField,
  NumberTaskFormikField,
  PhoneTaskFormikField,
  RatingTaskFormikField,
  ShortTextTaskFormikField,
  QuizTaskFormikField,
  TaskFormikMultipleChoiceType,
  TaskFormikQuizType,
  SignatureTaskFormikField,
  MatrixTaskFormikField,
} from '../../inputs/task-fields/TaskFormikFields';
import { TaskFormValuesType } from './model/task-form-init';
import { TaskFormItem } from './model/__generated__/TaskFormItem';

function getFirstField(taskItems: Array<TaskFormItem>): string {
  for (let i = 0; i < taskItems.length; i++) {
    if (taskItems[i].__typename === 'VOLUNTEER_TaskItemFieldType') {
      return taskItems[i].taskItemId;
    }
  }
  return '';
}

function getFieldBreakItemIds(taskItems: Array<TaskFormItem>): { [key: string]: boolean } {
  const itemIds: { [key: string]: boolean } = {};
  for (let i = taskItems.length - 1; i >= 0; i--) {
    if (i === taskItems.length - 1) {
      itemIds[taskItems[i].taskItemId] = taskItems[i].__typename !== 'VOLUNTEER_TaskItemFieldType';
      continue;
    }
    if (
      taskItems[i].__typename === 'VOLUNTEER_TaskItemFieldType' &&
      taskItems[i + 1].__typename !== 'VOLUNTEER_TaskItemFieldType'
    ) {
      itemIds[taskItems[i + 1].taskItemId] = true;
    }
  }
  return itemIds;
}

interface TaskItemFormFieldProps {
  taskItem: TaskFormItem;
  fieldBreakItemIds: { [key: string]: boolean };
  firstFieldItemId?: string;
  taskItems: TaskItemsDeserialize;
  values: TaskFormValuesType;
  completedTasks?: any[];
  fieldNamePrefix?: string;
}

const TaskItemFormField = ({
  taskItem,
  fieldBreakItemIds,
  firstFieldItemId,
  values,
  taskItems,
  completedTasks,
  fieldNamePrefix,
}: TaskItemFormFieldProps) => {
  const { lightTextColor } = getAlertCardColors('warning');
  if (taskItem.__typename === 'VOLUNTEER_TaskItemHeadingType') {
    return (
      <TaskItemHeader
        key={taskItem.taskItemId}
        isFieldBreak={fieldBreakItemIds[taskItem.taskItemId]}
        content={taskItem.heading}
      />
    );
  }

  if (taskItem.__typename === 'VOLUNTEER_TaskItemContentType') {
    const attachments: UploadedFile[] = taskItem.attachments.map((attachment) => {
      return { url: attachment.url, fileId: attachment.attachmentId, name: attachment.name };
    });

    return (
      <TaskItemContents
        key={taskItem.taskItemId}
        isFieldBreak={fieldBreakItemIds[taskItem.taskItemId]}
        draftJsRawContent={taskItem.content}
        attachments={attachments}
      />
    );
  }

  if (taskItem.__typename === 'VOLUNTEER_TaskItemMediaType') {
    return (
      <TaskItemMedia
        key={taskItem.taskItemId}
        isFieldBreak={fieldBreakItemIds[taskItem.taskItemId]}
        media={taskItem.media}
      />
    );
  }

  if (taskItem.__typename === 'VOLUNTEER_TaskItemFieldType') {
    const { field } = taskItem;
    const fieldType = field.__typename;
    const fieldName = fieldNamePrefix ? `${fieldNamePrefix}.${field.taskFieldId}` : field.taskFieldId;
    const ariaId = fieldName;
    const ariaDescribedBy = `${fieldName}-description`;

    if (fieldType === 'VOLUNTEER_ShortTextTaskFieldType') {
      return (
        <ShortTextTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          autoComplete='no'
          outlinedInputProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }

    if (fieldType === 'VOLUNTEER_LongTextTaskFieldType') {
      return (
        <LongTextTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          autoComplete='no'
          outlinedInputProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_NumberTaskFieldType') {
      return (
        <NumberTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          outlinedInputProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_MultipleChoiceTaskFieldType') {
      const selectedOptionId = (values[field.taskFieldId] as TaskFormikMultipleChoiceType)?.multipleChoiceOptionId;

      return (
        <>
          <MultipleChoiceTaskFormikField
            key={taskItem.taskItemId}
            name={fieldName}
            title={field.name}
            description={field.description}
            ariaId={ariaId}
            ariaDescribedBy={ariaDescribedBy}
            values={values}
            options={
              field.__typename === 'VOLUNTEER_MultipleChoiceTaskFieldType'
                ? field.multipleChoiceOptions
                    .sort((a, b) => (a.order > b.order ? 1 : -1))
                    .map((option) => ({
                      id: option.multipleChoiceFieldOptionId,
                      label: option.name,
                      allowText: option.allowText,
                    }))
                : []
            }
            required={!taskItem.optional}
          />
          {(taskItems.byTrigger[selectedOptionId]?.triggerItemIds || []).map((taskItemId) => (
            <TaskItemFormField
              key={taskItemId}
              taskItem={taskItems.byId[taskItemId]}
              taskItems={taskItems}
              values={values}
              fieldBreakItemIds={fieldBreakItemIds}
              firstFieldItemId={firstFieldItemId}
              completedTasks={completedTasks}
              fieldNamePrefix={fieldNamePrefix}
            />
          ))}
        </>
      );
    }
    if (fieldType === 'VOLUNTEER_CheckboxTaskFieldType') {
      const checkedOptionIds = (values[field.taskFieldId] || []) as string[];
      return (
        <>
          <CheckboxTaskFormikField
            key={taskItem.taskItemId}
            name={fieldName}
            title={field.name}
            description={field.description}
            ariaId={ariaId}
            ariaDescribedBy={ariaDescribedBy}
            options={
              field.__typename === 'VOLUNTEER_CheckboxTaskFieldType'
                ? field.checkboxOptions.map((option) => ({
                    id: option.checkboxFieldOptionId,
                    label: option.name,
                    allowText: option.allowText,
                  }))
                : []
            }
            required={!taskItem.optional}
          />
          {checkedOptionIds.map((triggerId) =>
            (taskItems.byTrigger[triggerId]?.triggerItemIds || []).map((taskItemId) => (
              <TaskItemFormField
                key={taskItemId}
                taskItem={taskItems.byId[taskItemId]}
                taskItems={taskItems}
                values={values}
                fieldBreakItemIds={fieldBreakItemIds}
                firstFieldItemId={firstFieldItemId}
                completedTasks={completedTasks}
                fieldNamePrefix={fieldNamePrefix}
              />
            ))
          )}
        </>
      );
    }
    if (fieldType === 'VOLUNTEER_QuizTaskFieldType') {
      const selectedOptionId = (values[field.taskFieldId] as TaskFormikQuizType)?.quizTaskFieldOptionId;

      return (
        <>
          <QuizTaskFormikField
            key={taskItem.taskItemId}
            name={fieldName}
            title={field.name}
            description={field.description}
            ariaId={ariaId}
            ariaDescribedBy={ariaDescribedBy}
            correctOptionId={
              field.__typename === 'VOLUNTEER_QuizTaskFieldType'
                ? field.quizTaskFieldCorrect?.quizTaskFieldOption.quizTaskFieldOptionId
                : ''
            }
            options={
              field.__typename === 'VOLUNTEER_QuizTaskFieldType'
                ? field.quizTaskFieldOptions
                    .sort((a, b) => (a.order > b.order ? 1 : -1))
                    .map((option) => ({
                      id: option.quizTaskFieldOptionId,
                      label: option.name,
                      allowText: option.allowText,
                    }))
                : []
            }
            required={!taskItem.optional}
          />
          {(taskItems.byTrigger[selectedOptionId]?.triggerItemIds || []).map((taskItemId) => (
            <TaskItemFormField
              key={taskItemId}
              taskItem={taskItems.byId[taskItemId]}
              taskItems={taskItems}
              values={values}
              fieldBreakItemIds={fieldBreakItemIds}
              firstFieldItemId={firstFieldItemId}
              completedTasks={completedTasks}
              fieldNamePrefix={fieldNamePrefix}
            />
          ))}
        </>
      );
    }
    if (fieldType === 'VOLUNTEER_DropdownTaskFieldType') {
      const dropdownValue = values[field.taskFieldId] as { dropdownOptionId: string } | undefined;
      const selectedDropdownOptionId = dropdownValue?.dropdownOptionId || '';
      return (
        <>
          <DropdownTaskFormikField
            key={taskItem.taskItemId}
            name={fieldName}
            title={field.name}
            description={field.description}
            ariaId={ariaId}
            ariaDescribedBy={ariaDescribedBy}
            options={
              field.__typename === 'VOLUNTEER_DropdownTaskFieldType'
                ? field.dropdownOptions
                    .sort((a, b) => (a.order > b.order ? 1 : -1))
                    .map((option) => ({
                      id: option.dropdownFieldOptionId,
                      label: option.name,
                      allowText: option.allowText,
                    }))
                : []
            }
            required={!taskItem.optional}
            freeSolo={field.__typename === 'VOLUNTEER_DropdownTaskFieldType' ? field.freeSolo : false}
            selectProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
          />
          {(taskItems.byTrigger[selectedDropdownOptionId]?.triggerItemIds || []).map((taskItemId) => (
            <TaskItemFormField
              key={taskItemId}
              taskItem={taskItems.byId[taskItemId]}
              taskItems={taskItems}
              values={values}
              fieldBreakItemIds={fieldBreakItemIds}
              firstFieldItemId={firstFieldItemId}
              completedTasks={completedTasks}
              fieldNamePrefix={fieldNamePrefix}
            />
          ))}
        </>
      );
    }
    if (fieldType === 'VOLUNTEER_AttachmentTaskFieldType') {
      return (
        <AttachmentTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          allowMultiple={field.__typename === 'VOLUNTEER_AttachmentTaskFieldType' ? field.allowMultiple : false}
          required={!taskItem.optional}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_DateTaskFieldType') {
      return (
        <DateTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          keyboardDatePickerProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_TimeTaskFieldType') {
      return (
        <TimeTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          keyboardTimePickerProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_AddressTaskFieldType') {
      return (
        <AddressTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          autoComplete='no'
          allowManual={field.__typename === 'VOLUNTEER_AddressTaskFieldType' ? field.allowManual : false}
          required={!taskItem.optional}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_PhoneTaskFieldType') {
      return (
        <PhoneTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          outlinedInputProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_EmailTaskFieldType') {
      return (
        <EmailTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          outlinedInputProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_UrlTaskFieldType') {
      return (
        <UrlTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          outlinedInputProps={taskItem.taskItemId === firstFieldItemId ? { autoFocus: true } : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_RatingTaskFieldType') {
      return (
        <RatingTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_YesNoTaskFieldType') {
      return (
        <YesNoTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_LinearScaleTaskFieldType') {
      return (
        <LinearScaleTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          minValue={field.__typename === 'VOLUNTEER_LinearScaleTaskFieldType' ? field.minValue : 1}
          maxValue={field.__typename === 'VOLUNTEER_LinearScaleTaskFieldType' ? field.maxValue : 5}
          minLabel={field.__typename === 'VOLUNTEER_LinearScaleTaskFieldType' ? field.minLabel : undefined}
          maxLabel={field.__typename === 'VOLUNTEER_LinearScaleTaskFieldType' ? field.maxLabel : undefined}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_NpsTaskFieldType') {
      return (
        <NpsTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_MatrixTaskFieldType') {
      return (
        <MatrixTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
          rows={field.__typename === 'VOLUNTEER_MatrixTaskFieldType' ? field.rows : []}
          columns={field.__typename === 'VOLUNTEER_MatrixTaskFieldType' ? field.columns : []}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_SignatureTaskFieldType') {
      return (
        <SignatureTaskFormikField
          key={taskItem.taskItemId}
          name={fieldName}
          title={field.name}
          description={field.description}
          ariaId={ariaId}
          ariaDescribedBy={ariaDescribedBy}
          required={!taskItem.optional}
        />
      );
    }
    if (fieldType === 'VOLUNTEER_CrossTaskDynamicTaskFieldType') {
      // For cross-task dynamic fields, we need to determine which option is active
      // based on the user's previous task completion data

      // Type guard to ensure we have the correct field type
      if (field.__typename !== 'VOLUNTEER_CrossTaskDynamicTaskFieldType') {
        return null;
      }

      // First check if we have the necessary data
      const { sourceTaskId } = field;

      // Find the source task in completed tasks
      const sourceCompletedTask = completedTasks?.find(
        (completedTask: any) => completedTask.task?.taskId === sourceTaskId
      );

      // If the source task isn't completed, show a message
      if (!sourceCompletedTask && sourceTaskId) {
        return (
          <Box key={taskItem.taskItemId} mb={2}>
            <Typography variant='body1' style={{ fontWeight: 600, marginBottom: 4 }}>
              {field.name}
            </Typography>
            {field.description && (
              <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
                {field.description}
              </Typography>
            )}
            <AlertCard variant='warning' title='Complete Previous Task First'>
              <Typography variant='body2' style={{ color: lightTextColor }}>
                This field depends on a selection from another task that needs to be completed first. Please complete
                the referenced task before filling out this form.
              </Typography>
            </AlertCard>
          </Box>
        );
      }

      const getCrossTaskActiveOption = (crossTaskField: any): string => {
        if (!crossTaskField) {
          return '';
        }
        if (!completedTasks || !crossTaskField.sourceTaskId || !crossTaskField.sourceFieldId) {
          return '';
        }

        // Find the field value that matches sourceFieldId
        const sourceFieldValue = sourceCompletedTask?.taskFieldValues?.find(
          (fieldValue: any) => fieldValue.field?.taskFieldId === crossTaskField.sourceFieldId
        );

        if (!sourceFieldValue) {
          return '';
        }

        // Extract the selected option based on field type
        switch (sourceFieldValue.__typename) {
          case 'VOLUNTEER_DropdownTaskFieldValueType':
            return sourceFieldValue.dropdownTaskFieldOption?.dropdownFieldOptionId || '';

          case 'VOLUNTEER_MultipleChoiceTaskFieldValueType':
            return sourceFieldValue.multipleChoiceTaskFieldOption?.multipleChoiceFieldOptionId || '';

          case 'VOLUNTEER_CheckboxTaskFieldValueType':
            // For checkbox fields, we'd need to handle multiple selections
            // For now, take the first one as an example
            return sourceFieldValue.checkboxTaskFieldOptions?.[0]?.checkboxFieldOptionId || '';

          case 'VOLUNTEER_QuizTaskFieldValueType':
            // Quiz fields might store the option ID differently
            return sourceFieldValue.quizTaskFieldOption?.quizTaskFieldOptionId || '';

          default:
            return '';
        }
      };

      const activeOptionValue = getCrossTaskActiveOption(field);

      return (
        <>
          {/* Render triggered content if there's an active option */}
          {activeOptionValue &&
            (taskItems.byTrigger[activeOptionValue]?.triggerItemIds || []).map((taskItemId) => (
              <TaskItemFormField
                key={taskItemId}
                taskItem={taskItems.byId[taskItemId]}
                taskItems={taskItems}
                values={values}
                fieldBreakItemIds={fieldBreakItemIds}
                firstFieldItemId={firstFieldItemId}
                completedTasks={completedTasks}
                fieldNamePrefix={fieldNamePrefix}
              />
            ))}
        </>
      );
    }
  }

  return null;
};

interface Props {
  taskItems: TaskItemsDeserialize;
  disableAutoFocus?: boolean;
  values: TaskFormValuesType;
  completedTasks?: any[]; // Array of completed tasks for cross-task field lookups
  allTasks?: any[]; // Array of all available tasks to get titles
  isTaskBlocked?: boolean; // Whether the task is blocked due to missing dependencies
  volunteerProgramIds?: string[]; // Program IDs the volunteer is enrolled in (for filtering hidden fields)
  fieldNamePrefix?: string; // Prefix for Formik field names (used by repeatable groups)
}

const TaskFormFieldList = ({
  taskItems,
  disableAutoFocus,
  values,
  completedTasks,
  allTasks,
  isTaskBlocked,
  volunteerProgramIds,
  fieldNamePrefix,
}: Props) => {
  const { lightTextColor } = getAlertCardColors('warning');
  const displayTaskItems = taskItems.allIds
    .filter((itemId) => !taskItems.byTriggerOption[itemId])
    .map((itemId) => taskItems.byId[itemId])
    .filter((taskItem) => {
      // Filter out items that are hidden for the volunteer's programs
      if (!volunteerProgramIds || !taskItem.hiddenForProgramIds || taskItem.hiddenForProgramIds.length === 0) {
        return true; // Show item if no filtering needed
      }
      // Hide item if any of the volunteer's program IDs are in the hidden list
      const shouldHide = volunteerProgramIds.some((programId) => taskItem.hiddenForProgramIds.includes(programId));
      return !shouldHide;
    });

  const [firstFieldItemId] = useState(disableAutoFocus ? undefined : getFirstField(displayTaskItems));
  const [fieldBreakItemIds] = useState(getFieldBreakItemIds(displayTaskItems));

  // If the task is blocked, show a warning message instead of the form fields
  if (isTaskBlocked) {
    // Find all the required source tasks for cross-task dynamic fields
    const requiredTasks = taskItems.allIds
      .map((itemId) => taskItems.byId[itemId])
      .filter(
        (item) =>
          item.__typename === 'VOLUNTEER_TaskItemFieldType' &&
          item.field?.__typename === 'VOLUNTEER_CrossTaskDynamicTaskFieldType'
      )
      .map((item) => {
        // Type guard - we know from the filter that this is a TaskItemFieldType
        if (item.__typename !== 'VOLUNTEER_TaskItemFieldType' || !item.field) {
          return null;
        }
        const field = item.field as any;
        // Try to find the source task from completed tasks first
        const completedSourceTask = completedTasks?.find((ct: any) => ct.task?.taskId === field.sourceTaskId)?.task;

        // If not found in completed tasks, look in all tasks
        const sourceTaskFromAll = allTasks?.find((task: any) => task.taskId === field.sourceTaskId);

        return {
          sourceTaskId: field.sourceTaskId,
          title: completedSourceTask?.title || sourceTaskFromAll?.title || 'Unknown Task',
        };
      })
      .filter((task): task is { sourceTaskId: string; title: string } => task !== null)
      .filter(
        (task, index, self) =>
          // Remove duplicates based on sourceTaskId
          index === self.findIndex((t) => t.sourceTaskId === task.sourceTaskId)
      );

    return (
      <Box display='flex' justifyContent='center' flex='1 1 auto'>
        <Box maxWidth={720} width={1} mt={3} mb={3}>
          <AlertCard variant='warning' title='Complete Previous Task First'>
            <Typography variant='body2' style={{ color: lightTextColor }}>
              This form depends on selections from other tasks that need to be completed first.
            </Typography>
            <Box mt={2}>
              <Typography variant='body2' style={{ color: lightTextColor, fontWeight: 600 }}>
                Required tasks:
              </Typography>
              <Box component='ul' mt={1} mb={0} pl={2} style={{ margin: 0 }}>
                {requiredTasks.map((task) => (
                  <Box component='li' key={task.sourceTaskId} style={{ marginBottom: '4px' }}>
                    <Typography variant='body2' style={{ color: lightTextColor }}>
                      {task.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </AlertCard>
        </Box>
      </Box>
    );
  }

  return (
    <Box display='flex' justifyContent='center' flex='1 1 auto'>
      <Box maxWidth={720} width={1}>
        {displayTaskItems.map((taskItem) => (
          <TaskItemFormField
            key={taskItem.taskItemId}
            taskItem={taskItem}
            firstFieldItemId={firstFieldItemId}
            fieldBreakItemIds={fieldBreakItemIds}
            values={values}
            taskItems={taskItems}
            completedTasks={completedTasks}
            fieldNamePrefix={fieldNamePrefix}
          />
        ))}
      </Box>
    </Box>
  );
};

interface TaskItemHeaderProps {
  isFieldBreak: boolean;
  content: string;
}

const TaskItemHeader = ({ isFieldBreak, content }: TaskItemHeaderProps) => (
  <Box display='flex' alignItems='center' alignContent='center' paddingLeft={1}>
    {isFieldBreak ? <ShortTextRounded style={{ color: '#9e9e9e', marginRight: 16 }} /> : null}
    <Typography variant='h6'>{content}</Typography>
  </Box>
);

interface TaskItemMediaProps {
  isFieldBreak: boolean;
  media: {
    __typename: string;
    name: string;
    description: string;
    videoId: string;
  };
}

const TaskItemMedia = ({ isFieldBreak, media }: TaskItemMediaProps) => {
  const videoUrl =
    media.__typename === 'VOLUNTEER_YoutubeMediaType'
      ? `https://www.youtube.com/watch?v=${media.videoId}`
      : `https://vimeo.com/${media.videoId}`;
  const { theme } = useCampfireTheme();
  return (
    <Box
      marginTop={2}
      marginBottom={3}
      paddingTop={3}
      paddingBottom={3}
      paddingLeft={4}
      paddingRight={2}
      minHeight={96}
      border={'1px solid #e9e9e9'}
      bgcolor='#ffffff'
      borderRadius={7}
    >
      <Box display='flex' alignItems='center' alignContent='center'>
        {isFieldBreak ? <ShortTextRounded style={{ color: '#9e9e9e', marginRight: 16 }} /> : null}
        <Typography style={{ fontWeight: 'bold', marginTop: '4px' }}>{media.name}</Typography>
      </Box>
      <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral400, marginTop: '4px' }}>
        {media.description}
      </Typography>
      <Box flex={1} mb={2}>
        <ReactPlayer url={videoUrl} width='100%' maxWidth='100%' controls />
      </Box>
    </Box>
  );
};

interface TaskItemContentsProps {
  isFieldBreak: boolean;
  draftJsRawContent: string;
  attachments: UploadedFile[];
}

const TaskItemContents = ({ isFieldBreak, draftJsRawContent, attachments }: TaskItemContentsProps) => (
  <Box display='flex' paddingLeft={1}>
    {isFieldBreak ? <ShortTextRounded style={{ color: '#9e9e9e', marginRight: 16, paddingTop: 8 }} /> : null}
    <Grid container direction='column' spacing={1}>
      <Grid item xs>
        <Typography style={{ paddingTop: 8, paddingBottom: 8 }} variant='body2' component='span' display='inline'>
          <CampfireRichEditor
            editorState={EditorState.createWithContent(convertFromRaw(JSON.parse(draftJsRawContent)))}
          />
        </Typography>
      </Grid>
      <Grid item xs>
        {attachments.length ? <FileUploadGallery allowMultiple uploadedFiles={attachments} viewOnly /> : null}
      </Grid>
    </Grid>
  </Box>
);

function isTaskItemFieldBreak(current: TaskFormItem, prev?: TaskFormItem) {
  if (prev === undefined && current.__typename !== 'VOLUNTEER_TaskItemFieldType') return true;

  if (prev === undefined) return false;

  return prev.__typename === 'VOLUNTEER_TaskItemFieldType' && current.__typename !== 'VOLUNTEER_TaskItemFieldType';
}

export { TaskFormFieldList, TaskItemHeader, TaskItemContents, isTaskItemFieldBreak, TaskItemMedia };
